import { useState, useEffect } from 'react'
import H from './data'
import HI from './components/Icons'
import {
  Av, Stars, StatusBadge, Delta, Kpi, Banner, Empty, SkeletonRows, BarChart, Sparkline,
  ConfirmDialog, Drawer, Sidebar, Topbar, LocationSwitcher, Toasts, CardHead, MobileNavDrawer, NAV
} from './components/SharedComponents'
import LoginScreen from './screens/Auth'
import Dashboard from './screens/Dashboard'
import Bookings from './screens/Bookings'
import Availability from './screens/Availability'
import Pricing from './screens/Pricing'
import Workspace from './screens/Workspace'
import Analytics from './screens/Analytics'
import Reviews from './screens/Reviews'
import { useAuth } from './hooks/useAuth'
import { useToast } from './hooks/useToast'
import { useNavigation } from './hooks/useNavigation'
import { initializeAnalytics } from './utils/analytics'

// Guest Dashboard Component
function GuestDashboard({ user, toast, onLogout }) {
  const [spaces] = useState([
    { id: 'sp_1', name: 'Loftworks Coffee', host: 'Athik Ahamed', city: 'Chennai', seats: 8, rating: 4.8, price: 499, thumb: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=300' },
    { id: 'sp_3', name: 'Indie Hub', host: 'Priya Kapoor', city: 'Bangalore', seats: 10, rating: 4.9, price: 599, thumb: 'https://images.unsplash.com/photo-1503387762-592f1a35be37?w=300' },
    { id: 'sp_5', name: 'Tech Commons', host: 'Raj Patel', city: 'Bangalore', seats: 7, rating: 4.6, price: 449, thumb: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=300' },
    { id: 'sp_7', name: 'CreativeLab Delhi', host: 'Vikram Singh', city: 'Delhi', seats: 12, rating: 4.85, price: 549, thumb: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=300' },
    { id: 'sp_10', name: 'Workspace Mumbai', host: 'Neha Gupta', city: 'Mumbai', seats: 6, rating: 4.7, price: 499, thumb: 'https://images.unsplash.com/photo-1497366216548-495f67f30480?w=300' },
    { id: 'sp_12', name: 'Coworking Hyderabad', host: 'Arun Kumar', city: 'Hyderabad', seats: 15, rating: 4.9, price: 399, thumb: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=300' },
  ])

  return (
    <div className="shell">
      <div className="main">
        <Topbar route="guest" notifications={[]} onNotif={() => { }} primaryAction={
          <button className="btn btn-ghost btn-sm" onClick={onLogout}>Sign out</button>
        } canGoBack={false} onBack={() => { }} onMenuOpen={() => { }} />
        <div className="main-scroll">
          <div className="page fade-in">
            {/* Header */}
            <div className="page-head" style={{ marginBottom: 32 }}>
              <div style={{ marginBottom: 16, width: '100%' }}>
                <div className="ttl" style={{ marginBottom: 4 }}>Welcome, {user.name}! 👋</div>
                <div className="sub">Find and book your perfect desk</div>
              </div>
            </div>

            {/* Spaces Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20, width: '100%' }}>
              {spaces.map(space => (
                <div key={space.id} className="card" style={{ overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column' }} onClick={() => toast('Opening space', space.name)}>
                  <div style={{ width: '100%', height: 180, background: 'var(--border)', backgroundImage: `url('${space.thumb}')`, backgroundSize: 'cover', backgroundPosition: 'center', flexShrink: 0 }} />
                  <div className="card-pad col g10" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div>
                      <div className="t-h3" style={{ marginBottom: 2 }}>{space.name}</div>
                      <div className="t-sm muted">{space.city} • Hosted by {space.host}</div>
                    </div>
                    <div style={{ marginTop: 'auto' }}>
                      <div className="row between" style={{ marginBottom: 12 }}>
                        <span className="t-sm"><strong>{space.rating}</strong> ⭐ • {space.seats} desks</span>
                        <span className="t-sm" style={{ color: 'var(--green)', fontWeight: 700 }}>₹{space.price}/hr</span>
                      </div>
                      <button className="btn btn-pri btn-block">Book Now</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Toasts items={[]} dismiss={() => { }} />
    </div>
  )
}

function HelpScreen({ toast }) {
  const topics = [
    [HI.calendar({ s: 18 }), "Managing bookings", "Confirm, modify, cancel and handle no-shows."],
    [HI.tag({ s: 18 }), "Pricing & payouts", "Set rates, run promos, understand fees."],
    [HI.clock({ s: 18 }), "Availability", "Opening hours, blocking dates, holidays."],
    [HI.star({ s: 18 }), "Reviews", "Responding well and lifting your rating."],
    [HI.shield({ s: 18 }), "Policies & disputes", "Cancellation rules and guest issues."],
    [HI.wallet({ s: 18 }), "Getting paid", "Payout schedule and bank details."],
  ];
  return (
    <div className="page fade-in">
      <div className="page-head"><div className="ttl">Help & resources</div><div className="sub">Guides, answers and a real human when you need one.</div></div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-pad row between wrap g16">
          <div className="row g12">
            <span style={{ width: 42, height: 42, borderRadius: 11, background: "var(--green-weak)", color: "var(--green)", display: "grid", placeItems: "center" }}>
              {HI.message({ s: 20 })}
            </span>
            <div><div className="t-h3">Talk to host support</div><div className="t-sm muted">Avg. reply under 30 min, 8am–10pm IST.</div></div>
          </div>
          <button className="btn btn-pri" onClick={() => toast("Chat started", "A host specialist will join shortly")}>Start a chat</button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
        {topics.map(([ic, t, d], i) => (
          <button key={i} className="card card-pad" style={{ textAlign: "left" }} onClick={() => toast("Opening guide", t)}>
            <span style={{ color: "var(--green)" }}>{ic}</span>
            <div className="t-h3" style={{ margin: "10px 0 4px" }}>{t}</div>
            <div className="t-sm muted" style={{ lineHeight: 1.5 }}>{d}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function App() {
  // Custom hooks for core logic
  const { user, handleLogin, logout } = useAuth()
  const { toasts, toast, dismiss } = useToast()
  const { route, setRoute, history, setHistory, go, goBack } = useNavigation()

  // Local component state
  const [location, setLocation] = useState(H.locations[0])
  const [locSwitch, setLocSwitch] = useState(false)
  const [actions, setActions] = useState(() => JSON.parse(localStorage.getItem("cocafe_actions") || JSON.stringify(H.actions)))
  const [notifications, setNotifications] = useState(() => JSON.parse(localStorage.getItem("cocafe_notifications") || JSON.stringify(H.notifications)))
  const [openBookingId, setOpenBookingId] = useState(null)
  const [locations, setLocations] = useState(H.locations)
  const [addingSpace, setAddingSpace] = useState(false)
  const [newSpaceName, setNewSpaceName] = useState("")
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Initialize analytics on mount
  useEffect(() => {
    initializeAnalytics()
  }, [])

  useEffect(() => { localStorage.setItem("cocafe_actions", JSON.stringify(actions)); }, [actions])
  useEffect(() => { localStorage.setItem("cocafe_notifications", JSON.stringify(notifications)); }, [notifications])

  const resolveAction = (id, msg) => {
    setActions((a) => a.filter((x) => x.id !== id))
    toast(msg)
  }
  const onNotif = (cmd) => { if (cmd === "read-all") { setNotifications((n) => n.map((x) => ({ ...x, unread: false }))); toast("All caught up"); } }

  const handleLoginWrapper = async (creds) => {
    try {
      await handleLogin(creds)
      toast("Signed in", `Welcome ${creds.name || creds.email}!`)
      go("dashboard")
    } catch (err) {
      throw new Error(err.message)
    }
  }

  const handleLogoutWrapper = () => {
    try {
      console.log('🔴 LOGOUT STARTED');

      // Clear localStorage first
      localStorage.removeItem('cocafe_token');
      localStorage.removeItem('cocafe_user');
      localStorage.removeItem('cocafe_version');
      console.log('✅ localStorage cleared');

      // Set route to login FIRST (this shows login screen immediately)
      setRoute("login");
      setHistory(["login"]);
      console.log('✅ Route set to login');

      // Call logout from useAuth (clears user state)
      logout();
      console.log('✅ logout() called');

      // Show confirmation
      toast("Signed out", "You've been logged out successfully");
      console.log('✅ Toast shown');
      console.log('✅ LOGOUT COMPLETE');
    } catch (error) {
      console.error('❌ LOGOUT ERROR:', error);
      // Force logout even if there's an error
      setRoute("login");
      setHistory(["login"]);
      localStorage.clear();
    }
  }
  const handleAddSpace = () => {
    if (!newSpaceName.trim()) return;
    const newLoc = { id: `loc_${Date.now()}`, name: newSpaceName, area: "New area", city: "Chennai", address: "TBA", thumb: H.locations[0].thumb, status: "paused", seats: 4, rating: 0, reviews: 0 };
    setLocations([...locations, newLoc]);
    setLocation(newLoc);
    setAddingSpace(false);
    setNewSpaceName("");
    toast("Space added", newSpaceName + " added to your account");
  }

  const openBooking = (bid) => { setOpenBookingId(bid); go("bookings"); }

  // CRITICAL: Check route FIRST - if route is "login", show login screen regardless of user state
  // This ensures logout works even if user state hasn't been cleared yet
  if (route === "login" || !user) {
    return <LoginScreen onLogin={handleLoginWrapper} />
  }

  // Guest user - show guest dashboard
  if (user.role === 'guest') return <GuestDashboard user={user} toast={toast} onLogout={handleLogoutWrapper} />

  const primaryAction = {
    dashboard: null,
    bookings: null,
    reviews: null,
  }[route] || null;

  let screen = null
  if (route === "dashboard") screen = <Dashboard go={go} toast={toast} openBooking={openBooking} actions={actions} resolveAction={resolveAction} user={user} />;
  else if (route === "bookings") screen = <Bookings toast={toast} openId={openBookingId} clearOpen={() => setOpenBookingId(null)} />;
  else if (route === "availability") screen = <Availability toast={toast} />;
  else if (route === "pricing") screen = <Pricing toast={toast} />;
  else if (route === "workspace") screen = <Workspace toast={toast} />;
  else if (route === "analytics") screen = <Analytics toast={toast} />;
  else if (route === "reviews") screen = <Reviews toast={toast} user={user} />;
  else if (route === "help") screen = <HelpScreen toast={toast} />;
  else screen = <Dashboard go={go} toast={toast} openBooking={openBooking} actions={actions} resolveAction={resolveAction} />;

  // Host user - show host dashboard
  return (
    <div className={`shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar
        route={route}
        go={go}
        location={location}
        openLocSwitch={() => setLocSwitch(true)}
        actionsCount={actions.length}
        onLogout={handleLogoutWrapper}
        user={user}
        isCollapsed={sidebarCollapsed}
      />
      <div className="main">
        <Topbar
          route={route}
          notifications={notifications}
          onNotif={onNotif}
          primaryAction={primaryAction}
          canGoBack={history.length > 1}
          onBack={goBack}
          onMenuOpen={() => setMobileMenuOpen(true)}
          onLogout={handleLogoutWrapper}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <div className="main-scroll">{screen}</div>
      </div>
      <MobileNavDrawer isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} route={route} go={go} actionsCount={actions.length} />

      {locSwitch && <LocationSwitcher current={location} locations={locations} onClose={() => setLocSwitch(false)} onAddSpace={() => { setAddingSpace(true); setLocSwitch(false); }}
        onPick={(l) => { setLocation(l); setLocSwitch(false); toast("Switched to " + l.name, l.area + ", " + l.city); }} />}

      {addingSpace && (
        <div className="modal">
          <div className="scrim" onClick={() => setAddingSpace(false)} />
          <div className="modal-card">
            <div className="modal-b">
              <div className="t-h2" style={{ marginBottom: 14 }}>Add a new space</div>
              <div className="col g14">
                <div className="field"><span className="lbl">Space name</span><input className="inp" placeholder="e.g., Downtown Coffee Lounge" value={newSpaceName} onChange={(e) => setNewSpaceName(e.target.value)} autoFocus /></div>
                <div className="field"><span className="lbl">Area</span><input className="inp" placeholder="e.g., Downtown" /></div>
                <div className="field"><span className="lbl">Address</span><input className="inp" placeholder="Full address" /></div>
              </div>
            </div>
            <div className="modal-f">
              <button className="btn btn-ghost" onClick={() => { setAddingSpace(false); setNewSpaceName(""); }}>Cancel</button>
              <button className="btn btn-pri" disabled={!newSpaceName.trim()} onClick={handleAddSpace}>Create space</button>
            </div>
          </div>
        </div>
      )}

      <Toasts items={toasts} dismiss={dismiss} />
    </div>
  )
}
